import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api, { getFallbackData } from '../../app/api';
import { setAlert } from '../alert/alertSlice';

const initialState = {
  papers: [],
  paper: null,
  loading: true,
  error: null,
  pagination: null,
  total: 0
};

// Get all papers with pagination
export const getPapers = createAsyncThunk(
  'papers/getPapers',
  async (params = {}, { rejectWithValue }) => {
    const { page = 1, limit = 10, search = '', year = '', journal = '' } = params;
    
    try {
      const res = await api.get('/papers', {
        params: { page, limit, search, year, journal }
      });
      return res.data;
    } catch (err) {
      if (!err.response) {
        // If we have a network error, return fallback data
        return getFallbackData('/papers');
      }
      return rejectWithValue(
        err.response?.data?.error || 'Error fetching papers'
      );
    }
  }
);

// Get single paper
export const getPaper = createAsyncThunk(
  'papers/getPaper',
  async (id, { rejectWithValue }) => {
    try {
      const res = await api.get(`/papers/${id}`);
      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.error || 'Error fetching paper'
      );
    }
  }
);

// Create paper
export const createPaper = createAsyncThunk(
  'papers/createPaper',
  async (paperData, { rejectWithValue, dispatch }) => {
    try {
      const res = await api.post('/papers', paperData);
      dispatch(setAlert({ msg: 'Paper created successfully', type: 'success' }));
      return res.data;
    } catch (err) {
      const message = 
        err.response?.data?.error || 
        err.response?.data?.errors?.[0]?.msg || 
        'Error creating paper';
      
      dispatch(setAlert({ msg: message, type: 'error' }));
      return rejectWithValue(message);
    }
  }
);

// Update paper
export const updatePaper = createAsyncThunk(
  'papers/updatePaper',
  async ({ id, paperData }, { rejectWithValue, dispatch }) => {
    try {
      const res = await api.put(`/papers/${id}`, paperData);
      dispatch(setAlert({ msg: 'Paper updated successfully', type: 'success' }));
      return res.data;
    } catch (err) {
      const message = 
        err.response?.data?.error || 
        err.response?.data?.errors?.[0]?.msg || 
        'Error updating paper';
      
      dispatch(setAlert({ msg: message, type: 'error' }));
      return rejectWithValue(message);
    }
  }
);

// Delete paper
export const deletePaper = createAsyncThunk(
  'papers/deletePaper',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      await api.delete(`/papers/${id}`);
      dispatch(setAlert({ msg: 'Paper deleted successfully', type: 'success' }));
      return id;
    } catch (err) {
      const message = 
        err.response?.data?.error || 
        'Error deleting paper';
      
      dispatch(setAlert({ msg: message, type: 'error' }));
      return rejectWithValue(message);
    }
  }
);

const paperSlice = createSlice({
  name: 'papers',
  initialState,
  reducers: {
    clearPaper: (state) => {
      state.paper = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get papers
      .addCase(getPapers.pending, (state) => {
        state.loading = true;
      })
      .addCase(getPapers.fulfilled, (state, action) => {
        state.loading = false;
        state.papers = action.payload.data;
        state.pagination = action.payload.pagination;
        state.total = action.payload.total;
      })
      .addCase(getPapers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Get single paper
      .addCase(getPaper.pending, (state) => {
        state.loading = true;
      })
      .addCase(getPaper.fulfilled, (state, action) => {
        state.loading = false;
        state.paper = action.payload.data;
      })
      .addCase(getPaper.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Create paper
      .addCase(createPaper.pending, (state) => {
        state.loading = true;
      })
      .addCase(createPaper.fulfilled, (state, action) => {
        state.loading = false;
        state.papers.unshift(action.payload.data);
      })
      .addCase(createPaper.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Update paper
      .addCase(updatePaper.pending, (state) => {
        state.loading = true;
      })
      .addCase(updatePaper.fulfilled, (state, action) => {
        state.loading = false;
        state.paper = action.payload.data;
        state.papers = state.papers.map(paper => 
          paper._id === action.payload.data._id ? action.payload.data : paper
        );
      })
      .addCase(updatePaper.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Delete paper
      .addCase(deletePaper.pending, (state) => {
        state.loading = true;
      })
      .addCase(deletePaper.fulfilled, (state, action) => {
        state.loading = false;
        state.papers = state.papers.filter(paper => paper._id !== action.payload);
        if (state.paper && state.paper._id === action.payload) {
          state.paper = null;
        }
      })
      .addCase(deletePaper.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { clearPaper, clearError } = paperSlice.actions;

export default paperSlice.reducer; 